import { FC } from "react";

import { brand } from "@topi/ui/styles/tokens/tokens";

/** File each illustration is read from, inside the topi folder and any set. */
const illustrationFiles = {
  billing: "billing.svg",
  cashFlow: "cashflow.svg",
  delivery: "delivery.svg",
  devices: "devices.svg",
  done: "done.svg",
  empty: "empty.svg",
  error: "error.svg",
  errorCircle: "error_circle.svg",
  feedback: "feedback.svg",
  idCheck: "id_check.svg",
  mail: "mail.svg",
  monthly: "monthly.svg",
  notFound: "not-found.svg",
  payment: "payment.svg",
  relax: "relax.svg",
  sent: "sent.svg",
  signUp: "signup.svg",
  success: "success.svg",
  successCircle: "success_circle.svg",
  transparency: "transparency.svg",
  transparency2: "transparency2.svg",
  warningCircle: "warning-circle.svg",
  warningCircleRed: "warning-circle-red.svg",
  warranty: "warranty.svg",
};

export type IllustrationNames = keyof typeof illustrationFiles;

export const illustrationNames = Object.keys(
  illustrationFiles
) as IllustrationNames[];

/**
 * URL of every illustration file, keyed by `<set>/<file>`. The topi set lives
 * in `topi/`; any other folder is a set named after a brand's `short_name`.
 * Imported `?no-inline` so small files are not inlined in the bundle, where
 * every brand would download them.
 */
export const illustrationUrls: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob<string>("@topi/ui/assets/illustrations/*/*.svg", {
      eager: true,
      import: "default",
      query: "?no-inline",
    })
  ).map(([path, url]) => [path.split("/").slice(-2).join("/"), url])
);

const fileUrl = (
  set: string,
  name: IllustrationNames,
  urls: Record<string, string>
): string | undefined => urls[`${set}/${illustrationFiles[name]}`];

/**
 * URL of an illustration for a set. Falls back to the topi drawing, per file,
 * when the set lacks that file or does not exist.
 */
export const resolveIllustration = (
  name: IllustrationNames,
  set: string,
  urls: Record<string, string> = illustrationUrls
): string | undefined =>
  fileUrl(set, name, urls) ?? fileUrl("topi", name, urls);

interface IllustrationProps {
  name: IllustrationNames;
  className?: string;
}

export const Illustration: FC<IllustrationProps> = ({
  name,
  className,
}: IllustrationProps) => (
  <img
    src={resolveIllustration(name, brand.shortName)}
    className={className}
    loading="lazy"
    alt=""
  />
);
